/**
 * Compact Chocolate Explorer – Product Page Widget
 * Simply Prestige · 2026
 *
 * Inline mini-quiz reusing the Flavor Finder's weighted tag scoring.
 * Progressive reveal: each answered step collapses, next appears below.
 *
 * Flow:
 *   Default path: Q1 (occasion) -> Q2 (flavor) -> results
 *   Gift path:    Q1 (gift)     -> budget     -> Q2 (flavor) -> results
 */

class CompactExplorer extends HTMLElement {
  constructor() {
    super();
    this.answers = {};
    this.profile = {};
    this.isGiftPath = false;
    this.budgetRange = null;
    this.collectedTags = [];
    this.filterGroups = [];
    this.stepOrder = ['1', '2'];
  }

  connectedCallback() {
    this.collection = this.dataset.collection || '';
    this.collapsedEl = this.querySelector('[data-collapsed]');
    this.expandedEl = this.querySelector('[data-expanded]');
    this.resultsEl = this.querySelector('[data-results]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.resultsGrid = this.querySelector('[data-results-grid]');
    this.profileTitle = this.querySelector('[data-profile-title]');
    this.bindEvents();
  }

  bindEvents() {
    /* Expand on collapsed click */
    if (this.collapsedEl) {
      this.collapsedEl.addEventListener('click', () => this.expand());
    }

    /* Pill clicks */
    this.querySelectorAll('.cex__pill').forEach(pill => {
      pill.addEventListener('click', e => this.handlePillClick(e));
    });

    /* Edit/undo clicks */
    this.querySelectorAll('.cex__answer-edit').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const step = btn.closest('.cex__step').dataset.step;
        this.undoFromStep(step);
      });
    });

    /* Restart */
    const restartBtn = this.querySelector('.cex__restart');
    if (restartBtn) {
      restartBtn.addEventListener('click', () => this.restart());
    }

    /* ATC delegation */
    this.addEventListener('click', e => {
      const btn = e.target.closest('.cex__atc');
      if (btn) this.handleATC(btn);
    });
  }

  /* ─── Expand / Collapse ─── */

  expand() {
    this.collapsedEl.hidden = true;
    this.expandedEl.hidden = false;
    /* Show first step */
    const first = this.querySelector('.cex__step[data-step="1"]');
    if (first) first.hidden = false;
  }

  collapse() {
    this.expandedEl.hidden = true;
    this.collapsedEl.hidden = false;
  }

  /* ─── Pill handling ─── */

  handlePillClick(e) {
    const pill = e.currentTarget;
    const stepEl = pill.closest('.cex__step');
    const step = stepEl.dataset.step;

    /* Visual select */
    stepEl.querySelectorAll('.cex__pill').forEach(p => p.classList.remove('is-selected'));
    pill.classList.add('is-selected');

    /* Store answer */
    if (step === 'budget') {
      this.budgetRange = {
        min: parseFloat(pill.dataset.priceMin) || 0,
        max: parseFloat(pill.dataset.priceMax) || Infinity,
      };
    } else {
      const tags = pill.dataset.tags ? pill.dataset.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const weight = parseInt(pill.dataset.weight) || 1;
      const profile = pill.dataset.profile || '';
      const isFilter = pill.dataset.filter === 'true';
      this.answers[step] = { tags, weight, profile, isFilter };
      this.profile[parseInt(step)] = profile;
    }

    /* Lock step after short delay */
    setTimeout(() => {
      this.lockStep(stepEl, pill.textContent.trim());
      this.advanceFrom(step);
    }, 200);
  }

  lockStep(stepEl, answerText) {
    stepEl.dataset.answered = 'true';
    const tag = stepEl.querySelector('.cex__answer-tag');
    if (tag) {
      tag.textContent = answerText;
    }
  }

  unlockStep(stepEl) {
    delete stepEl.dataset.answered;
    stepEl.querySelectorAll('.cex__pill').forEach(p => p.classList.remove('is-selected'));
  }

  /* ─── Navigation ─── */

  advanceFrom(step) {
    /* Gift path: Q1(gift) -> budget -> Q2 */
    if (step === '1') {
      const profile = this.answers['1']?.profile;
      if (profile === 'gift') {
        this.isGiftPath = true;
        this.showStep('budget');
        return;
      }
      this.isGiftPath = false;
      this.budgetRange = null;
      this.hideStep('budget');
      this.showStep('2');
      return;
    }

    if (step === 'budget') {
      this.showStep('2');
      return;
    }

    /* Q2 is the final step — advance directly to results */
    if (step === '2') {
      this.showResults();
    }
  }

  showStep(step) {
    const el = this.querySelector(`.cex__step[data-step="${step}"]`);
    if (el) {
      el.hidden = false;
      /* Smooth scroll into view */
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }

  hideStep(step) {
    const el = this.querySelector(`.cex__step[data-step="${step}"]`);
    if (el) {
      el.hidden = true;
      this.unlockStep(el);
    }
  }

  undoFromStep(step) {
    /* Determine which steps to clear */
    const allSteps = this.isGiftPath
      ? ['1', 'budget', '2']
      : ['1', '2'];

    const idx = allSteps.indexOf(step);
    if (idx === -1) return;

    /* Clear this step and all subsequent */
    for (let i = idx; i < allSteps.length; i++) {
      const s = allSteps[i];
      const el = this.querySelector(`.cex__step[data-step="${s}"]`);
      if (!el) continue;

      if (i === idx) {
        /* Re-open this step */
        this.unlockStep(el);
        el.hidden = false;
      } else {
        /* Hide subsequent steps */
        el.hidden = true;
        this.unlockStep(el);
      }

      /* Clear stored data */
      if (s === 'budget') {
        this.budgetRange = null;
      } else {
        delete this.answers[s];
        delete this.profile[parseInt(s)];
      }
    }

    /* If undoing from step 1, reset gift path */
    if (step === '1') {
      this.isGiftPath = false;
      this.budgetRange = null;
      this.hideStep('budget');
    }

    /* Hide results if showing */
    if (this.resultsEl) this.resultsEl.hidden = true;
    if (this.loadingEl) this.loadingEl.hidden = true;
  }

  /* ─── Results ─── */

  async showResults() {
    /* Build tag list + filters */
    this.collectedTags = [];
    this.filterGroups = [];

    Object.values(this.answers).forEach(a => {
      if (a.isFilter && a.tags.length > 0) {
        this.filterGroups.push(a.tags.map(t => t.toLowerCase()));
      }
      /* ALL tags contribute to scoring (including filter tags) */
      a.tags.forEach(tag => {
        for (let i = 0; i < a.weight; i++) {
          this.collectedTags.push(tag.toLowerCase());
        }
      });
    });

    /* Show loading */
    if (this.loadingEl) this.loadingEl.hidden = false;

    try {
      const products = await this.fetchProducts();
      const matched = this.matchProducts(products);
      this.setProfileText();
      this.renderResults(matched);

      await this.delay(1000);

      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = false;
    } catch (err) {
      console.error('Compact Explorer error:', err);
      await this.delay(500);
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = false;
      if (this.resultsGrid) {
        const L = (window.SC_FF_LABELS || {});
        this.resultsGrid.innerHTML = `<p style="font-size:0.8125rem;color:rgb(var(--text-color)/.5)">${L.fetchError || 'Kunne ikke hente produkter.'}</p>`;
      }
    }
  }

  setProfileText() {
    /* Title is fixed — we keep this hook in case we want dynamic copy later */
    const L = (window.SC_FF_LABELS || {});
    if (this.profileTitle) this.profileTitle.textContent = L.recommendationsTitle || 'Dine anbefalinger';
  }

  /* ─── Fetch & Score (identical to FlavorFinder) ─── */

  async fetchProducts() {
    const url = this.collection
      ? `/collections/${this.collection}/products.json?limit=250`
      : '/products.json?limit=250';

    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.products;
  }

  matchProducts(products) {
    const scored = products.map(product => {
      const ptags = product.tags.map(t => t.toLowerCase().trim());
      let score = 0;
      this.collectedTags.forEach(tag => {
        if (ptags.includes(tag)) score++;
      });

      /* Filter-bonus: products matching MORE filter-group tags rank higher.
         E.g. protein bar with 5 protein tags -> 500 bonus vs gift box with 1 -> 100 */
      let filterBonus = 0;
      this.filterGroups.forEach(group => {
        group.forEach(ft => {
          if (ptags.includes(ft)) filterBonus++;
        });
      });

      return { ...product, matchScore: score + (filterBonus * 100) };
    });

    let results = scored.filter(p => p.matchScore > 0);

    /* Hard filters */
    if (this.filterGroups.length > 0) {
      const filtered = results.filter(p => {
        const ptags = p.tags.map(t => t.toLowerCase().trim());
        return this.filterGroups.every(group =>
          group.some(ft => ptags.includes(ft))
        );
      });
      if (filtered.length > 0) results = filtered;
    }

    /* Budget filter */
    if (this.budgetRange) {
      const { min, max } = this.budgetRange;
      const priceFiltered = results.filter(p => {
        const price = parseFloat(p.variants[0].price);
        return price >= min && price <= max;
      });
      if (priceFiltered.length > 0) results = priceFiltered;
    }

    return results
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 3);
  }

  /* ─── Render ─── */

  renderResults(products) {
    if (!this.resultsGrid) return;

    const L = (window.SC_FF_LABELS || {});

    if (!products.length) {
      this.resultsGrid.innerHTML = `<p style="font-size:0.8125rem;color:rgb(var(--text-color)/.5)">${L.noResults || 'Ingen produkter matchede dine præferencer.'}</p>`;
      return;
    }

    const maxScore = this.collectedTags.length || 1;

    this.resultsGrid.innerHTML = products.map((p, i) => {
      const img = p.images[0]?.src || '';
      const price = this.formatMoney(p.variants[0].price);
      const variantId = p.variants[0].id;
      const title = this.escapeHtml(p.title);
      const url = `/products/${p.handle}`;

      return `
        <div class="cex__product">
          <a href="${url}" class="cex__product-figure" data-instant>
            ${img ? `<img src="${img}&width=200" alt="${title}" loading="lazy" width="100" height="100">` : ''}
          </a>
          <div class="cex__product-info">
            <a href="${url}" class="cex__product-title" data-instant>${title}</a>
            <p class="cex__product-price">${price}</p>
          </div>
          <button type="button" class="cex__atc" data-variant-id="${variantId}" aria-label="${L.addToCartAria || 'Tilføj'} ${title}">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4.75 8.25A.75.75 0 0 0 4 9L3 19.125c0 1.418 1.207 2.625 2.625 2.625h12.75c1.418 0 2.625-1.149 2.625-2.566L20 9a.75.75 0 0 0-.75-.75H4.75Zm2.75 0v-1.5a4.5 4.5 0 0 1 4.5-4.5v0a4.5 4.5 0 0 1 4.5 4.5v1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 12v6M9 15h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>`;
    }).join('');
  }

  /* ─── Add to Cart (Prestige cart-drawer integration) ─── */

  async handleATC(btn) {
    if (btn.classList.contains('is-loading')) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-dasharray="60" stroke-dashoffset="0"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur=".8s" repeatCount="indefinite"/></circle></svg>';

    try {
      /* Prestige uses bundled sections for cart drawer */
      const sections = 'cart-drawer,cart-drawer-recommendations';

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId), quantity: 1 }],
          sections: sections,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

      /* Dispatch events for Prestige theme cart drawer */
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add' }
      }));

      /* Update cart drawer sections if available */
      if (data.sections) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change:sections', {
          bubbles: true,
          detail: { sections: data.sections }
        }));
      }

      /* Open cart drawer */
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      }

      /* Reset button after delay */
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      }, 2000);

    } catch (err) {
      console.error('ATC error:', err);
      const L = (window.SC_FF_LABELS || {});
      btn.classList.remove('is-loading');
      btn.textContent = L.errorRetry || 'Fejl — prøv igen';
      setTimeout(() => { btn.textContent = L.addToCart || 'Læg i kurv'; }, 2000);
    }
  }

  /* ─── Restart ─── */

  restart() {
    /* Reset all state */
    this.answers = {};
    this.profile = {};
    this.isGiftPath = false;
    this.budgetRange = null;
    this.collectedTags = [];
    this.filterGroups = [];

    /* Reset all steps */
    this.querySelectorAll('.cex__step').forEach(step => {
      this.unlockStep(step);
      if (step.dataset.step !== '1') {
        step.hidden = true;
      }
    });

    /* Hide results & loading */
    if (this.resultsEl) this.resultsEl.hidden = true;
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (this.resultsGrid) this.resultsGrid.innerHTML = '';

    /* Back to collapsed */
    this.collapse();
  }

  /* ─── Helpers ─── */

  formatMoney(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '';
    const L = (window.SC_FF_LABELS || {});
    const cents = Math.round(num * 100);
    /* Samme visning som resten af shoppen: moneySample er {{ 1234567 | money_with_currency }},
       fx "12.345,67 DKK" eller "€12.345,67 EUR". Tegn og placering læses derfra. */
    const m = /^(.*?)12(\D?)345(\D)67(.*)$/.exec(L.moneySample || '');
    if (!m) return (cents / 100).toFixed(2).replace('.', ',') + ' ' + (L.currencyCode || 'DKK');
    const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, m[2]);
    return m[1] + whole + m[3] + String(cents % 100).padStart(2, '0') + m[4];
  }

  escapeHtml(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  delay(ms) {
    return new Promise(r => setTimeout(r, ms));
  }
}

if (!customElements.get('compact-explorer')) {
  customElements.define('compact-explorer', CompactExplorer);
}
